import { useContext } from "preact/hooks";
import { EditionContext } from "../hooks/useEdition";

/**
 * Join a base path with a sub-route without producing double slashes.
 * Handles the root base path ("/") used when the widget is on the homepage,
 * where naive concatenation would yield "//artist/123".
 */
export function joinPath(base: string, sub: string): string {
  const cleanBase = base === "/" ? "" : base.replace(/\/+$/, "");
  const cleanSub = sub.replace(/^\/+/, "");
  return `${cleanBase}/${cleanSub}`;
}

export function useNavigation() {
  const context = useContext(EditionContext);
  if (!context) {
    throw new Error("useNavigation must be used within EditionProvider");
  }

  const { basePath, detailsBasePath, routingMode } = context;

  if (routingMode === "hash") {
    return {
      toArtist: (id: string) => `#/artist/${id}`,
      toEvent: (id: string) => `#/event/${id}`,
      toPlace: (id: string) => `#/place/${id}`,
      toList: () => "#/",
    };
  }

  return {
    // Use detailsBasePath for artist/event detail pages (canonical path)
    toArtist: (id: string) => joinPath(detailsBasePath, `artist/${id}`),
    toEvent: (id: string) => joinPath(detailsBasePath, `event/${id}`),
    toPlace: (id: string) => joinPath(basePath, `place/${id}`),
    toList: () => basePath || "/",
  };
}

export function parseSubRoute(subRoute: string): {
  type: "artist" | "event" | "place" | "list";
  id?: string;
} {
  if (!subRoute) {
    return { type: "list" };
  }

  const parts = subRoute.split("/");

  // Check for artist/{id}, event/{id}, or place/{id} anywhere in path
  for (let i = 0; i < parts.length - 1; i++) {
    if (parts[i] === "artist" && parts[i + 1]) {
      return { type: "artist", id: parts[i + 1] };
    }
    if (parts[i] === "event" && parts[i + 1]) {
      return { type: "event", id: parts[i + 1] };
    }
    if (parts[i] === "place" && parts[i + 1]) {
      return { type: "place", id: parts[i + 1] };
    }
  }

  return { type: "list" };
}

export function parseQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const search = window.location.search.slice(1);
  if (!search) return params;

  for (const pair of search.split("&")) {
    const [key, value] = pair.split("=");
    if (key) {
      params[decodeURIComponent(key)] = decodeURIComponent(value ?? "");
    }
  }
  return params;
}

export function updateQueryParams(updates: Record<string, string | null>): void {
  const params = parseQueryParams();

  for (const [key, value] of Object.entries(updates)) {
    if (value === null || value === "") {
      delete params[key];
    } else {
      params[key] = value;
    }
  }

  const search = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  const url = window.location.pathname + (search ? `?${search}` : "");
  try {
    history.replaceState(null, "", url);
  } catch {
    // Ignore - history API blocked in iframe context (e.g., Elementor preview)
  }
}
